import PriceFormat from "./priceFormat";

/**
 * Part priority scoring (0-100) and tier helpers.
 *
 * The backend will eventually return a `priorityScore` plus a
 * `priorityBreakdown` per part / part category. The total drives which
 * tier the part lands in; the breakdown is the per-component decomposition
 * we surface in the hover popover so the user can see *why* a part is
 * scored where it is.
 *
 *   Part value score:        0 – 25
 *   Low stock score:         0 – 25
 *   Sales demand score:      0 – 20
 *   Sell-through score:      0 – 15
 *   Vehicle commonality:     0 – 10
 *   Listing age adjustment: -20 – +5
 *   ─────────────────────────────────
 *   Total (clamped 0-100)
 *
 * Tier bands (0-100) — "Do not remove" is the floor band; everything
 * above 40 climbs in 15-point increments up to "Priority" (the top tier
 * that should always be on a recommended job).
 *
 *   86-100 → Priority
 *   71-85  → High
 *   56-70  → Medium
 *   41-55  → Low
 *    0-40  → Do not remove
 */
export type PriorityTierId =
  | "doNotRemove"
  | "low"
  | "medium"
  | "high"
  | "priority";

export interface PriorityTierMeta {
  id: PriorityTierId;
  /** Long form, e.g. "Do not remove". */
  label: string;
  /** One-word form for compact chips. */
  shortLabel: string;
  min: number;
  max: number;
  /** 1 = top tier. Used so "Priority" sorts above "High" etc. */
  rank: number;
  /** Tailwind classes for tier-coloured chips and bars. */
  chipClass: string;
  barClass: string;
  buttonIdleClass: string;
  buttonSelectedClass: string;
  /** Tailwind background used in the stacked distribution bar. */
  segmentClass: string;
}

/** Ordered top-down — `PRIORITY_TIERS[0]` is the highest tier. */
export const PRIORITY_TIERS: PriorityTierMeta[] = [
  {
    id: "priority",
    label: "Priority",
    shortLabel: "Priority",
    min: 86,
    max: 100,
    rank: 1,
    chipClass:
      "bg-emerald-50 text-emerald-700 ring-emerald-200",
    barClass: "bg-emerald-500",
    buttonIdleClass:
      "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50",
    buttonSelectedClass:
      "border-emerald-500 bg-emerald-600 text-white shadow-sm",
    segmentClass: "bg-emerald-500",
  },
  {
    id: "high",
    label: "High",
    shortLabel: "High",
    min: 71,
    max: 85,
    rank: 2,
    chipClass: "bg-sky-50 text-sky-700 ring-sky-200",
    barClass: "bg-sky-500",
    buttonIdleClass:
      "border-sky-200 bg-white text-sky-700 hover:bg-sky-50",
    buttonSelectedClass:
      "border-sky-500 bg-sky-600 text-white shadow-sm",
    segmentClass: "bg-sky-500",
  },
  {
    id: "medium",
    label: "Medium",
    shortLabel: "Medium",
    min: 56,
    max: 70,
    rank: 3,
    chipClass: "bg-amber-50 text-amber-800 ring-amber-200",
    barClass: "bg-amber-500",
    buttonIdleClass:
      "border-amber-200 bg-white text-amber-800 hover:bg-amber-50",
    buttonSelectedClass:
      "border-amber-500 bg-amber-500 text-white shadow-sm",
    segmentClass: "bg-amber-400",
  },
  {
    id: "low",
    label: "Low",
    shortLabel: "Low",
    min: 41,
    max: 55,
    rank: 4,
    chipClass: "bg-orange-50 text-orange-800 ring-orange-200",
    barClass: "bg-orange-500",
    buttonIdleClass:
      "border-orange-200 bg-white text-orange-800 hover:bg-orange-50",
    buttonSelectedClass:
      "border-orange-500 bg-orange-500 text-white shadow-sm",
    segmentClass: "bg-orange-400",
  },
  {
    id: "doNotRemove",
    label: "Do not remove",
    shortLabel: "Skip",
    min: 0,
    max: 40,
    rank: 5,
    chipClass: "bg-slate-100 text-slate-600 ring-slate-200",
    barClass: "bg-slate-400",
    buttonIdleClass:
      "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
    buttonSelectedClass:
      "border-slate-500 bg-slate-600 text-white shadow-sm",
    segmentClass: "bg-slate-300",
  },
];

const TIERS_BY_ID: Record<PriorityTierId, PriorityTierMeta> =
  PRIORITY_TIERS.reduce(
    (acc, tier) => {
      acc[tier.id] = tier;
      return acc;
    },
    {} as Record<PriorityTierId, PriorityTierMeta>
  );

/** Returns the tier metadata that contains the given score. */
export function getPriorityTier(score: number): PriorityTierMeta {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  for (const tier of PRIORITY_TIERS) {
    if (clamped >= tier.min && clamped <= tier.max) return tier;
  }
  return TIERS_BY_ID.doNotRemove;
}

export function getPriorityTierById(id: PriorityTierId): PriorityTierMeta {
  return TIERS_BY_ID[id];
}

/**
 * "Recommended" = anything that isn't the bottom band. Used by the
 * quick-select button on the Create Jobs page to bulk-add every
 * category we'd actually want a dismantler to remove.
 */
export function isRecommendedTier(tier: PriorityTierMeta): boolean {
  return tier.id !== "doNotRemove";
}

/* ─────────────────────────────────────────────────────────────────────
 * Score breakdown — the six factors that compose the 0-100 priority
 * total. Each component reports both:
 *   - `score`: the numeric contribution to the total (e.g. 18/25).
 *   - `value`: the underlying metric the score was derived from
 *              (e.g. £250 part value, 2 in stock, 65% sell-through).
 *
 * The hover popover on the priority chip surfaces these so dismantlers
 * can see *why* a category is scored where it is.
 * ───────────────────────────────────────────────────────────────────── */
export type PriorityBreakdownKey =
  | "partValue"
  | "lowStock"
  | "salesDemand"
  | "sellThrough"
  | "vehicleRarity"
  | "listingAge";

export interface PriorityBreakdownComponent {
  /** Contribution to the total score for this component. */
  score: number;
  /** Underlying metric (price, count, percentage, days) used to compute `score`. */
  value: number;
}

export type PriorityBreakdown = Record<
  PriorityBreakdownKey,
  PriorityBreakdownComponent
>;

export interface PriorityComponentMeta {
  key: PriorityBreakdownKey;
  label: string;
  description: string;
  /** Minimum allowable score contribution. */
  min: number;
  /** Maximum allowable score contribution. */
  max: number;
  /** Formats the underlying `value` for display in the popover. */
  formatValue: (value: number) => string;
}

/** Ordered top-down — same order we use in the popover. */
export const PRIORITY_COMPONENTS: PriorityComponentMeta[] = [
  {
    key: "partValue",
    label: "Part value",
    description: "Average sold / market price for parts in this category.",
    min: 0,
    max: 25,
    formatValue: (v) => PriceFormat(v),
  },
  {
    key: "lowStock",
    label: "Low stock",
    description: "Higher score when we hold few or none of this part.",
    min: 0,
    max: 25,
    formatValue: (v) => `${Math.round(v).toLocaleString("en-GB")} in stock`,
  },
  {
    key: "salesDemand",
    label: "Sales demand",
    description: "Recent units sold across the last 90 days.",
    min: 0,
    max: 20,
    formatValue: (v) => `${Math.round(v).toLocaleString("en-GB")} sold (90d)`,
  },
  {
    key: "sellThrough",
    label: "Sell-through",
    description: "Sold ÷ listed for comparable parts.",
    min: 0,
    max: 15,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  {
    key: "vehicleRarity",
    label: "Vehicle rarity",
    description:
      "Higher score for rare donor vehicles — fewer of this make/model/mk processed means fewer future chances to stock these parts.",
    min: 0,
    max: 10,
    formatValue: (v) => {
      const count = Math.round(v);
      const noun = count === 1 ? "donor" : "donors";
      const suffix =
        count === 0
          ? " (first of its kind)"
          : count <= 3
            ? " (rare)"
            : count >= 50
              ? " (very common)"
              : count >= 15
                ? " (common)"
                : "";
      return `${count.toLocaleString("en-GB")} ${noun} processed${suffix}`;
    },
  },
  {
    key: "listingAge",
    label: "Listing age adjustment",
    description:
      "Penalty for stale stock (oldest live listings) — small uplift for fresh parts.",
    min: -20,
    max: 5,
    formatValue: (v) =>
      `${Math.round(v).toLocaleString("en-GB")}d oldest listing`,
  },
];

/** Sums the component scores (clamped to 0-100). */
export function totalScoreFromBreakdown(breakdown: PriorityBreakdown): number {
  const raw = PRIORITY_COMPONENTS.reduce(
    (sum, comp) => sum + (breakdown[comp.key]?.score ?? 0),
    0
  );
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/* ─────────────────────────────────────────────────────────────────────
 * Deterministic mock breakdown / score. Same FNV-1a + mulberry32 PRNG
 * used by `JobAssemblyStats` so the score is stable per category across
 * the session. Replace with the live `priorityBreakdown` from the API
 * as soon as the backend ships it.
 * ───────────────────────────────────────────────────────────────────── */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  min + rand() * (max - min);

/**
 * Deterministic mock priority breakdown for a category. The `seedExtra`
 * lets callers shift the distribution per-vehicle so two different donor
 * cars don't end up with identical recommendations.
 *
 * Each component scores between its declared min/max; the underlying
 * `value` is shaped to roughly correlate with the score so the popover
 * looks plausible (e.g. high "Low stock" score → small in-stock count).
 */
export function mockPriorityBreakdown(
  categoryId: string | null | undefined,
  seedExtra?: string
): PriorityBreakdown {
  const seed = `${categoryId ?? "unknown"}|${seedExtra ?? ""}|priority-v2`;
  const rand = seededRand(seed);

  // Rarity is a vehicle-level metric — every part on the same donor
  // should share the same rarity score / donor count. We seed rarity
  // off the `seedExtra` (vehicle id) only so all categories on a
  // given vehicle agree.
  const rarityRand = seededRand(`${seedExtra ?? "unknown"}|rarity-v1`);

  const partValueScore = Math.round(between(rand, 0, 25));
  const lowStockScore = Math.round(between(rand, 0, 25));
  const salesDemandScore = Math.round(between(rand, 0, 20));
  const sellThroughScore = Math.round(between(rand, 0, 15));
  const rarityScore = Math.round(between(rarityRand, 0, 10));
  const listingAgeScore = Math.round(between(rand, -20, 5));

  // Underlying values — broadly correlated with their score so the
  // popover "feels" like the score was derived from them.
  const partValue = Math.round(20 + (partValueScore / 25) * 1180);
  const inStock =
    lowStockScore >= 22
      ? 0
      : lowStockScore >= 15
        ? Math.max(1, Math.round(between(rand, 1, 2)))
        : lowStockScore >= 8
          ? Math.round(between(rand, 2, 6))
          : Math.round(between(rand, 6, 18));
  const salesLast90 = Math.round((salesDemandScore / 20) * 28 + rand() * 2);
  const sellThroughPct = Math.round((sellThroughScore / 15) * 95 + rand() * 5);
  // Rarity: high score → few donors processed (rare), low score → many.
  // The score field is inversely correlated with the donor count so the
  // popover reads as expected (e.g. "1 donor processed (rare)" → +10).
  // Seeded off the vehicle so every part on the same donor matches.
  const donorCount =
    rarityScore >= 9
      ? Math.round(between(rarityRand, 0, 1))
      : rarityScore >= 7
        ? Math.round(between(rarityRand, 1, 3))
        : rarityScore >= 4
          ? Math.round(between(rarityRand, 4, 14))
          : rarityScore >= 2
            ? Math.round(between(rarityRand, 15, 45))
            : Math.round(between(rarityRand, 50, 220));
  // Negative score → older listing; positive score → fresh listing.
  const listingAgeDays =
    listingAgeScore >= 0
      ? Math.round(between(rand, 1, 30))
      : Math.round(between(rand, 60, 360));

  return {
    partValue: { score: partValueScore, value: partValue },
    lowStock: { score: lowStockScore, value: inStock },
    salesDemand: { score: salesDemandScore, value: salesLast90 },
    sellThrough: { score: sellThroughScore, value: sellThroughPct },
    vehicleRarity: { score: rarityScore, value: donorCount },
    listingAge: { score: listingAgeScore, value: listingAgeDays },
  };
}

/**
 * Deterministic mock priority score (0-100). Derived from
 * `mockPriorityBreakdown` so the popover and the chip always agree on
 * the total.
 */
export function mockPriorityScore(
  categoryId: string | null | undefined,
  seedExtra?: string
): number {
  return totalScoreFromBreakdown(
    mockPriorityBreakdown(categoryId, seedExtra)
  );
}

/**
 * Convenience: deterministic mock tier for a category.
 */
export function mockPriorityTier(
  categoryId: string | null | undefined,
  seedExtra?: string
): PriorityTierMeta {
  return getPriorityTier(mockPriorityScore(categoryId, seedExtra));
}
